import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, CheckCircle2, Sparkles, RefreshCw } from 'lucide-react';
import confetti from 'canvas-confetti';

interface QRScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStampUnlocked: (id: number) => void;
  onViewStamp: (id: number) => void;
}

export const QRScannerModal: React.FC<QRScannerModalProps> = ({
  isOpen,
  onClose,
  onStampUnlocked,
  onViewStamp,
}) => {
  const [cameraActive, setCameraActive] = useState<boolean>(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [scanSuccess, setScanSuccess] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const startCamera = async () => {
    stopCamera();
    setCameraError(null);

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Thiết bị hoặc trình duyệt không hỗ trợ truy cập camera.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Không thể kết nối máy ảnh';
      setCameraError(errorMsg + '. Bạn vẫn có thể trải nghiệm bằng nút "Quét QR mẫu".');
      setCameraActive(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setScanSuccess(false);
      setIsScanning(false);
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen]);

  const handleDemoScan = () => {
    setIsScanning(true);
    setTimeout(() => {
      setIsScanning(false);
      setScanSuccess(true);
      onStampUnlocked(2); // Unlock Pho tuong Phat Ba Quan Am (id: 2)

      try {
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#a50021', '#e6ba55', '#246958'],
        });
      } catch (e) {
        // Confetti fallback
      }
    }, 1200);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-md animate-fade-in">
      <div 
        className="relative w-full max-w-xl bg-[#21171c] border border-[#9d7d49] rounded-2xl shadow-2xl overflow-hidden text-[#fff0dc]"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-[#685049] bg-[#281c21]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-[#3d1723] text-[#e6ba55] flex items-center justify-center font-bold">
              <Camera className="w-4 h-4" />
            </div>
            <strong className="text-lg font-serif font-bold text-white">Quét mã QR vật phẩm</strong>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-[#2d2026] hover:bg-[#3d2c34] text-[#fff0dc] border border-[#8e7148] flex items-center justify-center transition-all"
            aria-label="Đóng camera"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        {!scanSuccess ? (
          <div className="p-5 sm:p-6 flex flex-col items-center">
            {/* Viewfinder View */}
            <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-black flex items-center justify-center border border-white/20 shadow-inner">
              <video
                ref={videoRef}
                className={`absolute inset-0 w-full h-full object-cover ${cameraActive ? 'opacity-100' : 'opacity-0'}`}
                playsInline
                muted
                autoPlay
              />

              {!cameraActive && (
                <div className="relative z-10 px-6 text-center text-white/80 max-w-sm">
                  <Camera className="w-12 h-12 mx-auto mb-3 text-[#e6ba55]/70" />
                  <p className="text-sm font-medium mb-2 text-white">Khung ngắm quét QR</p>
                  <p className="text-xs text-white/60">
                    {cameraError || 'Cho phép truy cập máy ảnh và hướng ống kính vào bảng thông tin tại Bảo tàng.'}
                  </p>
                </div>
              )}

              {/* Viewfinder Target Frame */}
              <div className="absolute z-20 w-48 h-48 sm:w-56 sm:h-56 border-2 border-[#e6ba55] rounded-2xl pointer-events-none flex items-center justify-center shadow-[0_0_0_9999px_rgba(0,0,0,0.6)]">
                <div className="absolute inset-2 border border-dashed border-white/60 rounded-xl" />
                {/* Laser animation */}
                <div className="absolute left-4 right-4 h-0.5 bg-[#e6ba55] shadow-[0_0_12px_#e6ba55] animate-bounce" />
              </div>
            </div>

            {/* Status note */}
            <p className="mt-4 text-xs text-center text-[#cdbdaf]">
              {isScanning ? (
                <span className="text-[#e6ba55] font-bold flex items-center gap-1.5 animate-pulse">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  Đang nhận diện mã QR VNFAM-QB-003…
                </span>
              ) : (
                'Hướng khung ngắm vào mã QR dán tại chân bảng trưng bày hiện vật.'
              )}
            </p>

            {/* Actions */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3 w-full">
              <button
                onClick={startCamera}
                className="gf-button gf-button-secondary text-sm flex-1"
              >
                Khởi động lại máy ảnh
              </button>
              <button
                onClick={handleDemoScan}
                disabled={isScanning}
                className="gf-button text-sm flex-1 flex items-center justify-center gap-1.5"
              >
                <Sparkles className="w-4 h-4" />
                <span>Quét QR mẫu (QB-003)</span>
              </button>
            </div>
          </div>
        ) : (
          /* Success Screen */
          <div className="p-6 sm:p-8 text-center space-y-5 animate-scale-up">
            <div className="w-16 h-16 rounded-full bg-[#203e37] text-[#5b9e8b] border border-[#5b9e8b] flex items-center justify-center mx-auto shadow-xl">
              <CheckCircle2 className="w-10 h-10" />
            </div>

            <div>
              <span className="text-xs font-bold text-[#e6ba55] uppercase tracking-wider block">
                MÃ QR HỢP LỆ · BẢO TÀNG MỸ THUẬT VIỆT NAM
              </span>
              <h3 className="text-2xl font-serif font-bold text-white mt-1">
                Thu thập tem thành công!
              </h3>
              <p className="text-xs text-[#cdbdaf] mt-1 max-w-sm mx-auto">
                Bạn đã nhận diện thành công hiện vật <strong>Pho tượng Phật Bà Quan Âm</strong> và mở khóa con tem tương ứng.
              </p>
            </div>

            {/* Stamp Card Highlight */}
            <div className="p-4 rounded-2xl bg-[#281c21] border border-[#685049] flex items-center gap-4 text-left max-w-md mx-auto shadow-inner">
              <img
                src="/images/generated/stamps-v5/stamp-phat-ba-quan-am-v5.webp"
                alt="Pho tượng Phật Bà Quan Âm"
                className="w-16 h-20 object-contain drop-shadow"
              />
              <div>
                <span className="text-[10px] font-bold text-[#e6ba55] uppercase">
                  QB-003 · THẾ KỶ XVI
                </span>
                <h4 className="font-serif font-bold text-white text-base">
                  Pho tượng Phật Bà Quan Âm
                </h4>
                <p className="text-xs text-[#cdbdaf]">Gỗ sơn son thếp vàng</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => {
                  onClose();
                  onViewStamp(2);
                }}
                className="gf-button text-sm flex-1"
              >
                Xem chi tiết con tem
              </button>
              <button
                onClick={() => {
                  setScanSuccess(false);
                  startCamera();
                }}
                className="gf-button gf-button-secondary text-sm flex-1"
              >
                Quét hiện vật khác
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
